import { canEditLayout } from "../../utils/canEditLayout";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Download, ExternalLink, Upload, Loader2, ZoomIn } from "lucide-react";
import BlockShell from "./BlockShell";
import Lightbox from "../Lightbox";
import { Button } from "../ui/button";
import { useAuth, API } from "../../App";
import { absUrl } from "./media";
import ComingSoon from "./ComingSoon";

const MAX_BYTES = 40 * 1024 * 1024;

// A PDF (e.g. the original Outsider's Guide) with screenshots; editors upload the file from the page.
const DocumentBlock = ({ block, festival, layoutKey: layoutKeyProp }) => {
  const { user } = useAuth();
  const cfg = block.config || {};
  const layoutKey = layoutKeyProp || (festival?.slug ? `festival:${festival.slug}` : null);
  const canEdit = canEditLayout(user, layoutKey);
  const [fileUrl, setFileUrl] = useState(cfg.file_url || "");
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  const shots = (cfg.screenshots || []).filter(Boolean);
  const comingSoon = !fileUrl && !shots.length && Boolean(cfg.show_coming_soon);
  if (!fileUrl && !shots.length && !comingSoon && !canEdit) return null;
  const src = absUrl(fileUrl);

  const upload = async (file) => {
    if (!file || !layoutKey) return;
    if (!/pdf$/i.test(file.type) && !/\.pdf$/i.test(file.name)) return toast.error("Pick a PDF file");
    if (file.size > MAX_BYTES) return toast.error("PDF is over 40MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/document`, fd, { withCredentials: true });
      await axios.patch(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/blocks/${block.block_id}`, { config: { ...cfg, file_url: r.data.url } }, { withCredentials: true });
      setFileUrl(r.data.url);
      toast.success("PDF is live on the page");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Upload failed"); } finally { setBusy(false); }
  };

  const Uploader = ({ replace }) => (
    <label className={`inline-flex items-center gap-2 cursor-pointer rounded-full font-semibold text-sm px-4 py-2 border transition-colors ${busy ? "opacity-60 pointer-events-none" : ""} ${replace ? "bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200" : "bg-[#0A1626] text-[#F2CC8F] border-[#0A1626] hover:bg-[#1B3A5C]"}`} data-testid="document-upload-label">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      {busy ? "Uploading…" : replace ? "Replace the PDF" : "Upload the PDF (up to 40MB)"}
      <input type="file" accept="application/pdf,.pdf" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} data-testid="document-upload-input" />
    </label>
  );

  return (
    <section id={cfg.anchor || `document-${block.block_id}`} className="max-w-7xl mx-auto px-6 md:px-12 py-8 scroll-mt-24" data-testid="fest-document">
      <BlockShell block={block}>
        {cfg.intro && <p className="text-base md:text-lg text-[#3a3a3a] leading-relaxed mb-5 max-w-3xl whitespace-pre-line">{cfg.intro}</p>}
        {comingSoon ? (
          <ComingSoon cfg={cfg} kind="document">
            {canEdit && <><p className="text-xs text-[#5C5C5C] mb-3">Editors: upload the original PDF here. Add screenshots of its pages in <strong>Edit → Screenshots</strong>.</p><Uploader /></>}
          </ComingSoon>
        ) : (
          <div className="max-w-3xl rounded-2xl border border-[#E2E0DC] bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center gap-3">
              <span className="h-11 w-11 rounded-xl bg-[#B8860B]/15 text-[#B8860B] flex items-center justify-center shrink-0"><FileText className="h-5 w-5" /></span>
              <div className="flex-1 min-w-[12rem]">
                <div className="font-extrabold text-[#1A1A1A]">{cfg.file_label || "Original PDF"}</div>
                {cfg.caption && <p className="text-sm text-[#5C5C5C] mt-0.5 whitespace-pre-line">{cfg.caption}</p>}
              </div>
              {fileUrl ? (
                <div className="flex flex-wrap gap-2">
                  <a href={src} target="_blank" rel="noopener noreferrer"><Button size="sm" className="bg-[#0A1626] hover:bg-[#1B3A5C] text-white rounded-full" data-testid="document-open"><ExternalLink className="h-4 w-4 mr-2" /> Open the PDF</Button></a>
                  <a href={src} download target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="rounded-full" data-testid="document-download"><Download className="h-4 w-4 mr-2" /> Download</Button></a>
                  {canEdit && <Uploader replace />}
                </div>
              ) : canEdit ? <Uploader /> : null}
            </div>
            {fileUrl && cfg.show_preview && <iframe src={src} title={cfg.file_label || "PDF"} className="hidden md:block w-full h-[70vh] mt-5 rounded-xl border border-[#E2E0DC]" data-testid="document-preview" />}
          </div>
        )}
        {shots.length > 0 && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="document-screenshots">
            {shots.map((s, i) => (
              <button key={i} type="button" onClick={() => setZoom(absUrl(s))} className="group relative overflow-hidden rounded-xl border border-[#E2E0DC] bg-white shadow-sm cursor-zoom-in" data-testid="document-screenshot">
                <img src={absUrl(s)} alt={`Page ${i + 1}`} loading="lazy" className="w-full aspect-[3/4] object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" />
                <span className="absolute bottom-2 right-2 rounded-full bg-black/55 p-1.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"><ZoomIn className="h-3.5 w-3.5" /></span>
              </button>
            ))}
          </div>
        )}
        <Lightbox src={zoom} onClose={() => setZoom(null)} />
      </BlockShell>
    </section>
  );
};

export default DocumentBlock;
